<template>
  <div
    v-if="!isContentVisible"
    class="proofreading-select-type proofreading-select-type--locked"
    :class="{ 'proofreading-select-type--error': pendingWorkFetchFailed }"
    role="status"
    aria-live="polite"
  >
    <p v-if="pendingWorkFetchFailed" class="proofreading-select-type__fetch-error">
      {{ $t("proofreadingSelectTypePage.pendingWorkFetchError") }}
    </p>
    <LoadingSpinner v-else size="medium" variant="inline" />
  </div>

  <div v-else class="proofreading-select-type" :aria-busy="isProcessingCardClick ? 'true' : 'false'">
    <div class="proofreading-select-type__speech">
      <ChatMessage :message="$t('proofreadingSelectTypePage.speechMessage')" cat-icon-size="medium"
        class="proofreading-select-type__chat-message" />
    </div>

    <h1 class="proofreading-select-type__heading">
      {{ $t("proofreadingSelectTypePage.heading") }}
    </h1>

    <div class="proofreading-select-type__cards">
      <div class="type-card-wrapper">
        <button type="button" class="type-card type-card--active" @click="onClickBasic">
          <IconBook class="type-card__icon" aria-hidden="true" />
          <p class="type-card__title">{{ $t("proofreadingSelectTypePage.basicTitle") }}</p>
          <p class="type-card__description">{{ $t("proofreadingSelectTypePage.basicDescription") }}</p>
        </button>
      </div>

      <div class="type-card-wrapper">
        <button type="button" class="type-card type-card--active" @click="onClickAi">
          <IconRobot class="type-card__icon" aria-hidden="true" />
          <p class="type-card__title">{{ $t("proofreadingSelectTypePage.aiTitle") }}</p>
          <p class="type-card__description">{{ $t("proofreadingSelectTypePage.aiDescription") }}</p>
        </button>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import Vue from "vue";
import ChatMessage from "@/components/molecules/ChatMessage.vue";
import { useProofreadingBasicStore } from "@/stores/proofreadingBasic";
import { useProofreadingAiStore } from "@/stores/proofreadingAi";
import { cancelProofreadingBatch } from "@/lib/api/proofreadingAi";
import {
  deleteProofreadingWork,
  listProofreadingWorkManuscripts,
} from "@/lib/api/proofreading";
import { ensureManuscriptsLoaded } from "@/lib/utils/manuscriptLoader";
import { sortManuscriptKeys } from "@/lib/utils/proofreadingWorkLoad";
import { showNotifyDialog } from "@/lib/dialog";
import { Dialog } from "@/lib/utils/dialog";
import ProofreadingPendingWorkDialog, {
  ProofreadingPendingWorkDialogResult,
} from "@/components/ui/dialogs/ProofreadingPendingWorkDialog.vue";
import { ProofreadingAnalytics } from "@/lib/utils/analytics/proofreading";
import { resolveAiReviseSelectedManuscriptKeys } from "@/lib/utils/proofreadingReviseMode";
import {
  canStartNewProofreadingAfterDialog,
  classifyProofreadingSelectTypeEntry,
} from "@/lib/utils/proofreadingSelectTypeGate";
import LoadingSpinner from "@/components/atoms/LoadingSpinner.vue";
import IconBook from "~icons/figma/Book";
import IconRobot from "~icons/figma/Robot";

export default Vue.extend({
  components: {
    ChatMessage,
    LoadingSpinner,
    IconBook,
    IconRobot,
  },
  props: {
    novelId: String,
  },
  data() {
    return {
      analytics: null as ProofreadingAnalytics | null,
      proofreadingBasicStore: useProofreadingBasicStore(),
      proofreadingAiStore: useProofreadingAiStore(),
      isPendingWorkCheckComplete: false,
      isProcessingCardClick: false,
      pendingWorkFetchFailed: false,
    };
  },
  computed: {
    /** 初期判定が終わり、カードを描画してよいか。押下中も描画は維持する。 */
    isContentVisible(): boolean {
      return this.isPendingWorkCheckComplete;
    },
    canProceed(): boolean {
      return this.isPendingWorkCheckComplete && !this.isProcessingCardClick;
    },
  },
  mounted() {
    this.analytics = new ProofreadingAnalytics(this.$gtm);
    if (this.novelId) {
      this.analytics.trackOpen({ novelId: this.novelId });
    }

    this.checkPendingWorkData().catch((error) => {
      console.error("Failed to check pending proofreading work", error);
    });
  },
  methods: {
    /** 執筆画面の原稿 key 順（displayOrder）。 */
    manuscriptDisplayOrderKeys(): string[] {
      const manuscriptList =
        (this.$store.getters["manuscriptModule/manuscriptList"] as { key?: string }[]) ?? [];
      return manuscriptList
        .map((manuscript) => manuscript.key)
        .filter((key): key is string => typeof key === "string" && key.length > 0);
    },
    async checkPendingWorkData(): Promise<boolean> {
      if (!this.novelId) {
        this.isPendingWorkCheckComplete = true;
        return true;
      }

      try {
        const aiBatchStatus = await this.proofreadingAiStore.resolveStoredBatchStatus(this.novelId);

        const result = await listProofreadingWorkManuscripts(this.novelId);
        const { manuscriptIds } = result;
        const entry = classifyProofreadingSelectTypeEntry({
          aiBatchStatus,
          hasAiReviseResult: this.proofreadingAiStore.hasAiReviseResult(this.novelId),
          workManuscriptIds: manuscriptIds,
        });

        if (entry.kind === "waiting") {
          this.$router.replace({
            name: "proofreadingWaiting",
            params: { novelId: this.novelId },
          });
          // 遷移するので表示状態は変えない（初回はロックのまま、押下後はカードのまま）。
          return false;
        }

        if (entry.kind === "new") {
          this.isPendingWorkCheckComplete = true;
          return true;
        }

        const { canResumeAiRevise } = entry;

        const dialog = new Dialog(ProofreadingPendingWorkDialog);
        const dialogResult = (await dialog.show()) as ProofreadingPendingWorkDialogResult | undefined;

        if (dialogResult?.action === "yes") {
          if (canResumeAiRevise) {
            this.proofreadingAiStore.enterAiMode(this.novelId);
            await ensureManuscriptsLoaded(this.$store, this.novelId);
            const aiSelectedKeys = resolveAiReviseSelectedManuscriptKeys({
              storeKeys: this.proofreadingAiStore.selectedManuscriptKeys,
              aiResult: this.proofreadingAiStore.aiResult,
              workManuscriptIds: manuscriptIds,
              manuscriptKeyOrder: this.manuscriptDisplayOrderKeys(),
            });
            if (
              aiSelectedKeys.length > 0
              && this.proofreadingAiStore.selectedManuscriptKeys.length === 0
            ) {
              this.proofreadingAiStore.setSelectedManuscriptKeys(aiSelectedKeys);
            }
            // 結果が手元に無いときは待ち画面で finish API から取り直してから遷移する。
            this.$router.push(
              this.proofreadingAiStore.hasAiReviseResult(this.novelId)
                ? {
                  name: "proofreadingReviseManuscript",
                  params: { novelId: this.novelId },
                  query: { mode: "ai" },
                }
                : {
                  name: "proofreadingWaiting",
                  params: { novelId: this.novelId },
                },
            ).catch(() => undefined);
          } else {
            await ensureManuscriptsLoaded(this.$store, this.novelId);
            const manuscriptList =
              (this.$store.getters["manuscriptModule/manuscriptList"] as { key?: string }[]) ?? [];
            const sortedKeys = sortManuscriptKeys({
              keys: manuscriptIds,
              selectedManuscriptKeys: [],
              manuscriptList,
            });
            this.proofreadingAiStore.enterBasicMode(this.novelId);
            this.proofreadingBasicStore.setSelectedManuscriptKeys(sortedKeys);
            this.$router.push({
              name: "proofreadingReviseManuscript",
              params: { novelId: this.novelId },
            }).catch(() => undefined);
          }
          return false;
        }

        if (!canStartNewProofreadingAfterDialog(dialogResult?.action)) {
          // 再開か破棄を明示するまで新規校正は開始しない。カード押下でダイアログを出し直す。
          this.isPendingWorkCheckComplete = true;
          return false;
        }

        try {
          if (aiBatchStatus === "COMPLETED" && this.proofreadingAiStore.batchId) {
            await cancelProofreadingBatch(
              this.novelId,
              this.proofreadingAiStore.batchId,
              { discardCompleted: true },
            );
          }
          await deleteProofreadingWork(this.novelId);
          // 破棄を選んだので、未反映の AI 結果とバッチ追跡も残さない。
          this.proofreadingAiStore.clearAiReviseSession(this.novelId);
        } catch (error) {
          console.error("Failed to discard pending proofreading work", error);
          this.pendingWorkFetchFailed = true;
          await showNotifyDialog({
            content: this.$t("proofreadingSelectTypePage.pendingWorkDeleteError") as string,
          });
          this.isPendingWorkCheckComplete = false;
          return false;
        }
        this.isPendingWorkCheckComplete = true;
        return true;
      } catch (error) {
        console.error("Failed to fetch pending proofreading work", error);
        this.pendingWorkFetchFailed = true;
        await showNotifyDialog({
          content: this.$t("proofreadingSelectTypePage.pendingWorkFetchError") as string,
        });
        // 取得失敗時は canProceed を立てず、開始をブロックする
        this.isPendingWorkCheckComplete = false;
        return false;
      }
    },
    /**
     * カード押下時の再判定。
     *
     * 押下中にカードを隠すと消えて再表示されるちらつきになるため、表示は変えず
     * 二重押下だけを抑止する。
     */
    async runCardClick(proceed: (novelId: string) => void): Promise<void> {
      const { novelId } = this;
      if (!this.canProceed || !novelId) {
        return;
      }

      this.isProcessingCardClick = true;
      this.pendingWorkFetchFailed = false;
      try {
        if (!(await this.checkPendingWorkData())) {
          return;
        }
        proceed(novelId);
      } finally {
        this.isProcessingCardClick = false;
      }
    },
    async onClickBasic(): Promise<void> {
      await this.runCardClick((novelId) => {
        this.proofreadingAiStore.enterBasicMode(novelId);
        this.$router.push({
          name: "proofreadingSelectManuscript",
          params: { novelId },
        });
      });
    },
    async onClickAi(): Promise<void> {
      await this.runCardClick((novelId) => {
        this.proofreadingAiStore.enterAiMode(novelId);
        this.analytics?.trackAiStart({ novelId });
        this.$router.push({
          name: "proofreadingPreviewPlan",
          params: { novelId },
        });
      });
    },
  },
});
</script>

<style lang="scss" scoped>
@import "@/style-common.scss";

.proofreading-select-type {
  max-width: 640px;
  margin: 0 auto;
  padding: 40px 24px;
  color: var(--color-text-normal);
}

// 読み込み・遷移判定が終わるまでは中身を描画せず、中央のスピナーだけを見せる。
.proofreading-select-type--locked {
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  // 親の高さが auto になる SP でも中央に置けるよう vh で下限を持たせる。
  min-height: max(100%, 60vh);
  max-width: none;

  // 一瞬で終わるときにスピナーが瞬かないよう、少し遅れて出す。
  opacity: 0;
  animation: proofreading-select-type-loading-fade-in 0.3s ease 0.4s forwards;

  &.proofreading-select-type--error {
    opacity: 1;
    animation: none;
  }
}

.proofreading-select-type__speech {
  display: flex;
  justify-content: center;
  margin-bottom: 24px;

  ::v-deep .message-bubble {
    background-color: var(--color-surface-base);

    &::after {
      border-left-color: var(--color-surface-base);
      border-top: 5px solid transparent;
      border-bottom: 5px solid transparent;
      border-left: 5px solid var(--color-surface-base);
    }
  }
}

.proofreading-select-type__chat-message {
  max-width: 100%;
}

.proofreading-select-type__heading {
  margin: 0 0 32px;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
  text-align: left;
  color: var(--color-text-normal);
}

.proofreading-select-type__cards {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: flex-start;
  gap: 24px;
}

.proofreading-select-type--locked ::v-deep .loading-spinner-container--inline {
  padding: 0;

  .loading-spinner {
    margin-bottom: 0;
  }
}

.proofreading-select-type__fetch-error {
  margin: 0;
  font-size: var(--font-size-sm);
  line-height: var(--line-height-default);
  color: var(--color-error);
  text-align: center;
}

@keyframes proofreading-select-type-loading-fade-in {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

.type-card-wrapper {
  flex: 1 1 calc((100% - 24px) / 2);
  min-width: 260px;
  max-width: 100%;
  display: flex;
  flex-direction: column;
  align-items: stretch;
}

.type-card {
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  box-sizing: border-box;
  padding: 20px 16px;
  min-height: 160px;
  border: none;
  border-radius: var(--radius-lg);
  background-color: var(--color-surface-base);
  font: inherit;
  text-align: center;
  cursor: default;

  &--active {
    position: relative;
    cursor: pointer;

    @include hover-overlay;

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
      pointer-events: none;
    }
  }

  &--disabled {
    opacity: 0.55;
    pointer-events: none;
    background-color: var(--color-surface-base);
  }
}

.type-card__icon {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  margin: 0 0 16px;
  font-size: 48px;
  color: var(--color-text-normal);
}

.type-card--disabled .type-card__icon {
  color: var(--color-text-low);
}

.type-card__title {
  flex-shrink: 0;
  margin: 0 0 8px;
  min-height: calc(var(--font-size-md) * var(--line-height-default));
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-bold);
  line-height: var(--line-height-default);
  color: var(--color-text-normal);
}

.type-card--disabled .type-card__title,
.type-card--disabled .type-card__description {
  color: var(--color-text-low);
}

.type-card__description {
  margin: 0;
  font-size: var(--font-size-sm);
  line-height: var(--line-height-default);
  color: var(--color-text-normal);
}

@include mq("sp") {
  .proofreading-select-type {
    padding: 24px 16px;
  }

  .proofreading-select-type__cards {
    gap: 20px;
  }

  .type-card-wrapper {
    flex: 1 1 100%;
    min-width: 0;
  }
}
</style>
